import type { FeaturesContent } from '@mawkaa/types';
import { Check, Star, Zap, Shield, Clock, Users } from 'lucide-react';
import { cn } from '@mawkaa/ui';

const iconMap: Record<string, React.ElementType> = {
  check: Check,
  star: Star,
  zap: Zap,
  shield: Shield,
  clock: Clock,
  users: Users,
};

interface FeaturesBlockProps {
  content: FeaturesContent;
  isEditing?: boolean;
}

export function FeaturesBlock({ content, isEditing }: FeaturesBlockProps) {
  const { title, subtitle, features = [], columns = 3, style = 'cards' } = content;

  const gridClasses = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <section className="px-4 py-16">
      <div className="max-w-6xl mx-auto">
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && <h2 className="text-3xl font-bold mb-4">{title}</h2>}
            {subtitle && <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>}
          </div>
        )}

        <div className={cn('grid gap-8', gridClasses[columns])}>
          {features.length > 0 ? (
            features.map((feature, index) => {
              const Icon = iconMap[feature.icon || 'check'] || Check;

              return (
                <div
                  key={index}
                  className={cn(
                    'text-center',
                    style === 'cards' && 'bg-white p-6 rounded-lg shadow-sm border'
                  )}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              );
            })
          ) : isEditing ? (
            <div className="col-span-full text-center py-8 text-muted-foreground">
              Ajoutez des fonctionnalités dans le panneau de propriétés
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
